import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Select, type BadgeVariant } from '@silverfalls/sterling';
import { FilterInput } from '../components/FilterInput.js';
import { useToast } from '../components/Toast.js';
import { formatRelativeDate } from '../lib/format.js';
import { sortBy, toggleSort, type SortState } from '../lib/sort.js';
import {
  fetchPaymentRuns,
  schedulePaymentRun,
  type PaymentRunRow,
  type PaymentRunState,
} from '../api/p2p.js';

const REQUESTING_USER = 'u-ui-skeleton';

const STATE_FILTERS: ReadonlyArray<{ label: string; value: PaymentRunState | 'ALL' }> = [
  { label: 'All', value: 'ALL' },
  { label: 'Proposed', value: 'PROPOSED' },
  { label: 'Approved', value: 'APPROVED' },
  { label: 'Declined', value: 'DECLINED' },
  { label: 'Executing', value: 'EXECUTING' },
  { label: 'Executed', value: 'EXECUTED' },
  { label: 'Partially executed', value: 'PARTIALLY_EXECUTED' },
  { label: 'Reconciled', value: 'RECONCILED' },
];

type RunSortKey = 'scheduleDate' | 'state' | 'paymentCount' | 'netTotal' | 'createdAt';

function runSortAccessor(r: PaymentRunRow, key: RunSortKey): unknown {
  switch (key) {
    case 'scheduleDate':
      return r.scheduleDate;
    case 'state':
      return r.state;
    case 'paymentCount':
      return r.paymentCount;
    case 'netTotal':
      return Number(r.netTotal);
    case 'createdAt':
      return r.createdAt;
  }
}

function matchesQuery(r: PaymentRunRow, q: string): boolean {
  if (q === '') return true;
  const n = q.toLowerCase();
  return r.runRef.toLowerCase().includes(n) || r.state.toLowerCase().includes(n);
}

interface PaymentRunsProps {
  readonly opcoRef: string;
}

export function PaymentRuns({ opcoRef }: PaymentRunsProps): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const [runs, setRuns] = useState<ReadonlyArray<PaymentRunRow>>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [stateFilter, setStateFilter] = useState<PaymentRunState | 'ALL'>('ALL');
  const [sort, setSort] = useState<SortState<RunSortKey> | null>({
    key: 'createdAt',
    direction: 'desc',
  });
  const [scheduling, setScheduling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await fetchPaymentRuns(opcoRef);
      setRuns(rows);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [opcoRef]);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(() => {
    let rows = runs.filter((r) => matchesQuery(r, query));
    if (stateFilter !== 'ALL') rows = rows.filter((r) => r.state === stateFilter);
    return sortBy(rows, sort, runSortAccessor);
  }, [runs, query, stateFilter, sort]);

  const onSort = (key: RunSortKey) => setSort((prev) => toggleSort(prev, key));

  const proposedCount = runs.filter((r) => r.state === 'PROPOSED').length;

  const onSchedule = useCallback(async () => {
    setScheduling(true);
    try {
      const now = new Date();
      const result = await schedulePaymentRun({
        opcoRef,
        scheduleDate: now.toISOString(),
        lookaheadDays: 7,
        requestingUser: REQUESTING_USER,
      });
      if (result.__typename === 'SchedulePaymentRunFailure') {
        toast.push(`${result.class}: ${JSON.stringify(result.details)}`, 'error');
      } else {
        toast.push(
          `Scheduled — ${result.paymentCount} payment(s), net ${result.netTotal} ${result.currencyCode}`,
          'success',
        );
        await load();
      }
    } catch (err) {
      toast.push(err instanceof Error ? err.message : String(err), 'error');
    } finally {
      setScheduling(false);
    }
  }, [opcoRef, load, toast]);

  return (
    <section className="vendors-screen">
      <div className="screen-header">
        <div>
          <h2>Payment runs</h2>
          <p className="screen-header-desc">
            {runs.length} {runs.length === 1 ? 'run' : 'runs'}
            {proposedCount > 0 && ` · ${proposedCount} awaiting approval`}
          </p>
        </div>
        <Button
          variant="primary"
          icon="plus"
          onClick={onSchedule}
          loading={scheduling}
          title="Schedule a new payment run using the OPCO's config + a 7-day lookahead"
        >
          Schedule run
        </Button>
      </div>

      {loadError && (
        <div className="alert alert-neg" role="alert">
          Couldn't load runs: {loadError}
        </div>
      )}

      {loading ? (
        <div className="t-body-sm">Loading…</div>
      ) : runs.length === 0 ? (
        <div className="empty-state">
          <p>
            No payment runs in {opcoRef} yet. Click <strong>Schedule run</strong> to
            create one from the OPCO's currently APPROVED invoices.
          </p>
        </div>
      ) : (
        <>
          <div className="list-toolbar">
            <FilterInput
              value={query}
              onChange={setQuery}
              placeholder="Search by ref or state…"
              ariaLabel="Search payment runs"
            />
            <Select
              value={stateFilter}
              onValueChange={(v) => setStateFilter(v as PaymentRunState | 'ALL')}
              aria-label="State filter"
              options={STATE_FILTERS.map((f) => ({ value: f.value, label: f.label }))}
            />
            <span className="list-count t-body-sm">
              {visible.length} of {runs.length}
            </span>
          </div>

          {visible.length === 0 ? (
            <div className="empty-state">
              <p>No runs match.</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <SortHeader sort={sort} columnKey="scheduleDate" onSort={onSort}>
                    Scheduled
                  </SortHeader>
                  <SortHeader sort={sort} columnKey="state" onSort={onSort}>
                    State
                  </SortHeader>
                  <SortHeader sort={sort} columnKey="paymentCount" onSort={onSort}>
                    Payments
                  </SortHeader>
                  <SortHeader sort={sort} columnKey="netTotal" onSort={onSort}>
                    Net total
                  </SortHeader>
                  <SortHeader sort={sort} columnKey="createdAt" onSort={onSort}>
                    Created
                  </SortHeader>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => (
                  <tr
                    key={r.runRef}
                    className="row-clickable"
                    onClick={() => navigate(`/payment-runs/${r.runRef}`)}
                  >
                    <td>{formatRelativeDate(r.scheduleDate)}</td>
                    <td>
                      <Badge variant={runStateVariant(r.state)} dot={false}>
                        {r.state}
                      </Badge>
                    </td>
                    <td>{r.paymentCount}</td>
                    <td className="mono">
                      {r.netTotal} {r.currencyCode}
                    </td>
                    <td>{formatRelativeDate(r.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}

function runStateVariant(state: PaymentRunState): BadgeVariant {
  switch (state) {
    case 'PROPOSED':
      return 'info';
    case 'APPROVED':
    case 'EXECUTED':
    case 'RECONCILED':
      return 'success';
    case 'EXECUTING':
      return 'warning';
    case 'PARTIALLY_EXECUTED':
      return 'warning';
    case 'DECLINED':
      return 'neutral';
  }
}

interface SortHeaderProps {
  readonly sort: SortState<RunSortKey> | null;
  readonly columnKey: RunSortKey;
  readonly onSort: (key: RunSortKey) => void;
  readonly children: JSX.Element | string;
}

function SortHeader({ sort, columnKey, onSort, children }: SortHeaderProps): JSX.Element {
  const active = sort?.key === columnKey;
  const indicator = active ? (sort?.direction === 'asc' ? ' ▲' : ' ▼') : '';
  return (
    <th>
      <button
        type="button"
        className={`th-sort${active ? ' th-sort-active' : ''}`}
        onClick={() => onSort(columnKey)}
      >
        {children}
        <span className="th-sort-indicator" aria-hidden>
          {indicator}
        </span>
      </button>
    </th>
  );
}
